/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useLogin } from '@/hooks/useAuth'
import { toast } from 'sonner'

const loginSchema = z.object({
    email: z.string().email('Neplatný email'),
    password: z.string().min(8, 'Heslo musí mít alespoň 8 znaků'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
    const { mutate: login, isPending } = useLogin()
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
    })

    const onSubmit = (data: LoginFormData) => {
        login(data, {
            onError: (error: any) => {
                toast.error('Přihlášení selhalo', {
                    description: error.response?.data?.message || 'Neplatné přihlašovací údaje',
                })
            },
        })
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100">
            <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg">
                {/* Logo a název */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-primary mb-2">MENYXO</h1>
                    <p className="text-muted-foreground">Manage Your Matrix</p>
                </div>

                {/* Formulář */}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="vas@email.cz"
                            {...register('email')}
                            disabled={isPending}
                        />
                        {errors.email && (
                            <p className="text-sm text-destructive">{errors.email.message}</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="password">Heslo</Label>
                        <Input
                            id="password"
                            type="password"
                            placeholder="••••••••"
                            {...register('password')}
                            disabled={isPending}
                        />
                        {errors.password && (
                            <p className="text-sm text-destructive">{errors.password.message}</p>
                        )}
                    </div>

                    <Button type="submit" className="w-full" disabled={isPending}>
                        {isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Přihlašuji...
                            </>
                        ) : (
                            'Přihlásit se'
                        )}
                    </Button>
                </form>

                {/* Footer */}
                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>© 2026 Menyxo. Všechna práva vyhrazena.</p>
                </div>
            </div>
        </div>
    )
}
